import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCompany } from '../contexts/CompanyContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ServerClock from './ServerClock';
import ChatwootWidget from './ChatwootWidget';
import { 
  Clock, 
  LayoutDashboard, 
  Users, 
  ClipboardList, 
  Calendar, 
  CalendarDays,
  Umbrella,
  AlertTriangle,
  MapPin,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Building2,
  Crown,
  Fingerprint,
  ExternalLink,
  MessageSquareWarning,
  Bell,
  Shield,
  Loader2,
  PenLine,
  CreditCard,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const { user, logout, isAdmin, isSupervisor, isInspector, isSuperadmin, isWorker, currentOrganization } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { company, isSwitchingOrganization, refreshCompany } = useCompany();
  const location = useLocation();
  const navigate = useNavigate();

  // Track window width for responsive margin
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch unread notifications count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/unread-count`);
      setUnreadNotifications(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Refresh every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  // Refresh company data when component mounts or organization changes
  useEffect(() => {
    refreshCompany();
  }, [currentOrganization?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Superadmin is a platform-admin role, not a company user: its nav is limited
  // to managing organizations/plans and other superadmins, not day-to-day
  // operational data (employees, fichajes, turnos...) belonging to every tenant.
  const navItems = [
    { path: '/organizations', icon: Crown, label: language === 'es' ? 'Organizaciones' : 'Organizations', roles: ['superadmin'] },
    { path: '/superadmins', icon: Shield, label: language === 'es' ? 'Superadmins' : 'Superadmins', roles: ['superadmin'] },
    { path: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), roles: ['admin', 'supervisor', 'worker', 'inspector'] },
    { path: '/employees', icon: Users, label: t('employees'), roles: ['admin', 'supervisor', 'inspector', 'gestor'] },
    { path: '/time-records', icon: ClipboardList, label: t('time_records'), roles: ['admin', 'supervisor', 'worker', 'inspector'] },
    { path: '/shifts', icon: Calendar, label: t('shifts'), roles: ['admin', 'supervisor', 'inspector'] },
    { path: '/planning', icon: LayoutGrid, label: 'Planning', roles: ['admin', 'supervisor', 'inspector'] },
    { path: '/calendars', icon: CalendarDays, label: t('calendars'), roles: ['admin', 'supervisor', 'inspector'] },
    { path: '/vacations', icon: Umbrella, label: t('vacations'), roles: ['admin', 'supervisor', 'worker', 'inspector'] },
    { path: '/absences', icon: AlertTriangle, label: t('absences'), roles: ['admin', 'supervisor', 'inspector'] },
    { path: '/locations', icon: MapPin, label: t('locations'), roles: ['admin'] },
    { path: '/reports', icon: FileText, label: t('reports'), roles: ['admin', 'supervisor', 'worker', 'inspector', 'gestor'] },
    { path: '/export-templates', icon: FileSpreadsheet, label: language === 'es' ? 'Plantillas de Exportación' : 'Export Templates', roles: ['admin', 'gestor'] },
    { path: '/incidents', icon: MessageSquareWarning, label: language === 'es' ? 'Incidencias' : 'Incidents', roles: ['admin', 'supervisor', 'worker'] },
    { path: '/notifications', icon: Bell, label: language === 'es' ? 'Notificaciones' : 'Notifications', roles: ['admin', 'supervisor', 'worker'], badge: unreadNotifications },
    { path: '/my-shifts', icon: Calendar, label: language === 'es' ? 'Mis Turnos' : 'My Shifts', roles: ['worker'] },
    { path: '/mi-jornada', icon: PenLine, label: language === 'es' ? 'Mi Jornada' : 'My Timesheet', roles: ['worker'] },
    { path: '/kiosk', icon: Fingerprint, label: language === 'es' ? 'Fichar' : 'Clock In/Out', roles: ['worker'] },
    { path: '/settings', icon: Settings, label: t('settings'), roles: ['admin', 'superadmin'] },
    { path: '/settings/billing', icon: CreditCard, label: language === 'es' ? 'Facturación' : 'Billing', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item =>
    item.roles.includes(user?.role) &&
    !(item.path === '/kiosk' && company?.kiosk_enabled === false)
  );

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    // Special handling for Kiosk - open in new window and logout for security
    if (item.path === '/kiosk') {
      return (
        <button
          onClick={() => {
            const subdomain = currentOrganization?.subdomain || currentOrganization?.id;
            const kioskUrl = subdomain ? `/kiosk/${subdomain}` : '/kiosk';
            window.open(kioskUrl, '_blank', 'noopener,noreferrer');
          }}
          className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all w-full text-left ${
            'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
          } ${!sidebarOpen && !mobile ? 'justify-center' : ''}`}
          data-testid={`nav-${item.path.replace('/', '')}`}
        >
          <Icon className="w-5 h-5 flex-shrink-0" />
          {(sidebarOpen || mobile) && (
            <div className="flex items-center gap-2 flex-1">
              <span>{item.label}</span>
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </div>
          )}
        </button>
      );
    }
    
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all ${
          isActive 
            ? 'bg-indigo-50 text-indigo-700 font-medium' 
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        } ${!sidebarOpen && !mobile ? 'justify-center' : ''}`}
        data-testid={`nav-${item.path.replace('/', '')}`}
      >
        <div className="relative">
          <Icon className="w-5 h-5 flex-shrink-0" />
          {item.badge > 0 && !sidebarOpen && !mobile && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          )}
        </div>
        {(sidebarOpen || mobile) && (
          <div className="flex items-center gap-2 flex-1">
            <span>{item.label}</span>
            {item.badge > 0 && (
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">
                {item.badge > 99 ? '99+' : item.badge}
              </Badge>
            )}
          </div>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="main-layout">
      {isAdmin() && <ChatwootWidget />}
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="flex items-center gap-2">
            {company.company_logo ? (
              <img src={company.company_logo} alt="Logo" className="w-8 h-8 object-contain" />
            ) : (
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="font-bold font-['Manrope'] text-slate-900 truncate max-w-[150px]">
              {company.company_name || 'Regulisto'}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={toggleLanguage}>
          {language === 'es' ? 'EN' : 'ES'}
        </Button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`lg:hidden fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-slate-200 z-50 transform transition-transform ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <nav className="p-4 space-y-1">
          {filteredNavItems.map(item => (
            <NavLink key={item.path} item={item} mobile />
          ))}
          
          {/* Kiosk Button Mobile */}
          {(isAdmin() || isSuperadmin()) && company?.kiosk_enabled !== false && (
            <button
              onClick={() => {
                const subdomain = currentOrganization?.subdomain || currentOrganization?.id || 'default';
                const kioskUrl = `${window.location.origin}/kiosk/${subdomain}`;
                window.open(kioskUrl, '_blank', 'noopener,noreferrer');
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-md transition-all text-slate-600 hover:bg-green-50 hover:text-green-700 w-full"
              data-testid="open-kiosk-mobile-btn"
            >
              <Fingerprint className="w-5 h-5 flex-shrink-0" />
              <span className="flex items-center gap-1">
                Kiosko
                <ExternalLink className="w-3 h-3" />
              </span>
            </button>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-700 font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{t(user?.role)}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="mobile-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('logout')}
          </Button>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div 
        className={`hidden lg:flex fixed top-0 left-0 bottom-0 ${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-slate-200 flex-col transition-all duration-300 z-40`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              {company.company_logo ? (
                <img src={company.company_logo} alt="Logo" className="w-10 h-10 object-contain rounded-lg" />
              ) : (
                <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-lg font-bold font-['Manrope'] text-slate-900 truncate max-w-[140px]">
                {company.company_name || 'Regulisto'}
              </span>
            </div>
          ) : (
            company.company_logo ? (
              <img src={company.company_logo} alt="Logo" className="w-10 h-10 object-contain mx-auto" />
            ) : (
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center mx-auto">
                <Clock className="w-5 h-5 text-white" />
              </div>
            )
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex-shrink-0"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {filteredNavItems.map(item => (
            <NavLink key={item.path} item={item} />
          ))}
          
          {/* Kiosk Button - Only for admin/superadmin */}
          {(isAdmin() || isSuperadmin()) && company?.kiosk_enabled !== false && (
            <button
              onClick={() => {
                const subdomain = currentOrganization?.subdomain || currentOrganization?.id || 'default';
                const kioskUrl = `${window.location.origin}/kiosk/${subdomain}`;
                window.open(kioskUrl, '_blank', 'noopener,noreferrer');
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-md transition-all text-slate-600 hover:bg-green-50 hover:text-green-700 w-full"
              data-testid="open-kiosk-btn"
            >
              <Fingerprint className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <span className="flex items-center gap-1">
                  Kiosko
                  <ExternalLink className="w-3 h-3" />
                </span>
              )}
            </button>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          {/* Organization indicator for superadmin */}
          {sidebarOpen && user?.role === 'superadmin' && currentOrganization && (
            <div className="flex items-center gap-2 px-4 py-2 mb-2 bg-amber-50 rounded-lg">
              <Building2 className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-amber-700 truncate">{currentOrganization.name}</span>
            </div>
          )}
          {sidebarOpen && (
            <div 
              className="flex items-center gap-3 px-4 py-2 mb-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => navigate('/profile')}
              title={language === 'es' ? 'Ver perfil' : 'View profile'}
            >
              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-700 font-medium text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate hover:text-indigo-600">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{t(user?.role)}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={toggleLanguage}
              className={sidebarOpen ? '' : 'mx-auto'}
              data-testid="language-toggle-sidebar"
            >
              {language === 'es' ? 'EN' : 'ES'}
            </Button>
            {sidebarOpen && (
              <Button 
                variant="ghost" 
                size="sm"
                className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            )}
          </div>
          {!sidebarOpen && (
            <Button 
              variant="ghost" 
              size="sm"
              className="w-full mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main 
        className="min-h-screen pt-16 lg:pt-0 transition-all duration-300"
        style={{ marginLeft: windowWidth >= 1024 ? (sidebarOpen ? '256px' : '80px') : '0px' }}
      >
        {/* Server Time Display - Separate component to avoid re-rendering layout */}
        <ServerClock language={language} />
        
        {/* Loading overlay when switching organizations */}
        {isSwitchingOrganization && (
          <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="org-switch-loading">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <p className="text-slate-600 font-medium">
                {language === 'es' ? 'Cargando organización...' : 'Loading organization...'}
              </p>
            </div>
          </div>
        )}
        
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
